import { ReactNode, useMemo } from "react";
import { MenuItem2 } from "@blueprintjs/popover2";
import { ItemRenderer, Select2, Select2Props } from "@blueprintjs/select";
import { camelCaseToReadable } from "../utils/common";
import { PacketId, displayUnits } from "../utils/common-types";
import { packetThreadPoolApi, plotKeyToMultiMotorInfo, multiMotorPrefixFromNodeId } from "utils/packet-workers";
import { PlotKey } from "utils/packet.worker";
import { useSubActuators } from "hooks/useSubActuators";
import { keysOfIMultiMotorTelemetryMessage } from "connections/pscb";
import { SubActuators } from "utils/slnt-file";
import { actuatorDispositionStyle } from "design/colors";

const renderMessageKey: ItemRenderer<string> = ( key, { handleClick, handleFocus } ) => {
    const multiMotorInfo = plotKeyToMultiMotorInfo( key );
    const visibleName = multiMotorInfo?.baseName ?? key;
    const elementKey = multiMotorInfo?.plotKey ?? key;
    return (
        <MenuItem2
            key={ elementKey }
            icon={ multiMotorInfo?.label ? ( <span style={actuatorDispositionStyle}>{ multiMotorInfo.label } </span> ) : undefined }
            onClick={ handleClick }
            onFocus={ handleFocus }
            text={ camelCaseToReadable( visibleName ) }
        />
    );
};

export const MessageKeySelect = ( props: {
    onSelect: ( key: string ) => void;
    children: ReactNode;
    exclude?: (PlotKey | RegExp)[];
    nodeId?: number | "main";
    popoverRef?: Select2Props<string>[ "popoverRef" ];
    onClose?: () => void;
    onOpening?: () => void;
} ) => {

    const subActuators: SubActuators = useSubActuators();
    // TODO: make the following part of the return value of useSubActuators()
    const multiMotorKeys = useMemo( () => {
        let multiMotorKeys: string[] = [];
        for ( const actuatorInfo of Object.values( subActuators ) ) {
            const prefix = multiMotorPrefixFromNodeId( actuatorInfo.nodeId );
            for ( const k of keysOfIMultiMotorTelemetryMessage ) {
                multiMotorKeys.push( prefix + k );
            }
        }
        return multiMotorKeys;
    }, [ subActuators ] );

    const filteredKeys = useMemo(() => {
        const excludeKeys = props.exclude ?? [];
        const excludeKeyRegExps = excludeKeys.filter( x => x instanceof RegExp ) as RegExp[];
        const allTelemetryKeys = [
            ...Object.keys( displayUnits[ PacketId.REALTIME_DATA ] ),
            ...packetThreadPoolApi.getTelemSet().map( (element) => element.fieldName ),
            ...multiMotorKeys,
        ];
        return allTelemetryKeys.filter( k => {
            if ( excludeKeys.includes( k ) ) return false;
            for ( const re of excludeKeyRegExps ) {
                if ( re.test( k ) ) return false;
            }
            if ( props.nodeId === "main" ) return !k.startsWith( "MultiMotor_N" );
            if ( typeof props.nodeId === "number" ) return k.startsWith( multiMotorPrefixFromNodeId( props.nodeId ) );
            return true;
        } );
    }, [ props.exclude, props.nodeId, multiMotorKeys ] );

    if ( !filteredKeys.length ) return null;

    return (
        <Select2<string>
            items={ filteredKeys }
            itemRenderer={ renderMessageKey }
            onItemSelect={ k => props.onSelect( k ) }
            filterable={ false }
            popoverProps={{
                placement: "left",
                onClose: props.onClose,
                onOpening: props.onOpening,
            }}
            popoverRef={ props.popoverRef }
        >
            { props.children }
        </Select2>
    );
};
